import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { DripAudience, DripPreview, Option, PlaybookStep, WorkflowDraft, WorkflowDraftResult } from '../api/types'
import { Icon } from '../components/Icon'
import { PeoplePicker } from '../components/PeoplePicker'
import { PeopleTable, type FilterKey } from '../components/PeopleTable'
import { KIND } from '../components/StepKind'
import { ErrorState, Loading } from '../components/States'
import { useConfirm } from '../components/Confirm'
import { useToast } from '../components/Toast'
import { titleCase } from '../lib/format'
import { useVocabulary } from '../components/IntakeFields'
import { useAsync } from '../lib/useAsync'

// The builder is aimed at an officer who has never built an automation. So:
//   * every step is a plain sentence ("Send a text", "Wait 3 days"), never a node;
//   * personalisation is a button ("+ Their first name"), not {first_name} syntax;
//   * a live preview shows the schedule as the borrower would live it;
//   * mistakes are explained before Save, in the words the server would use.
// The server re-validates everything — this file only makes errors unlikely.

type StepType = PlaybookStep['type']
interface EditorStep extends PlaybookStep { uid: string }

const SAMPLE_NAME = 'Jordan'
const SMS_MAX = 480

let uidSeq = 0
const uid = () => `s${++uidSeq}`

const STEP_META: Record<StepType, { icon: string; label: string; hint: string }> = {
  task: { ...KIND.task, hint: 'A reminder on your task list — a call to make, a document to send.' },
  sms: { ...KIND.sms, hint: 'Goes out by itself, only to people who agreed to receive texts.' },
  email: { ...KIND.email, hint: 'Goes out by itself from your account.' },
  wait: { ...KIND.wait, hint: 'Pause before the next step.' },
}

const waitLabel = (d: number | undefined) =>
  !d ? '…' : d % 30 === 0 ? `${d / 30} month${d === 30 ? '' : 's'}` : d % 7 === 0 ? `${d / 7} week${d === 7 ? '' : 's'}` : `${d} day${d === 1 ? '' : 's'}`

/** One line that says what a step does, as the borrower-facing summary. */
function summary(s: PlaybookStep, officerFirst: string): string {
  if (s.type === 'task') return fill(s.title, officerFirst) || 'Say what the task is…'
  if (s.type === 'email') return fill(s.subject, officerFirst) || 'Add a subject…'
  if (s.type === 'sms') return s.body ? `“${fill(s.body, officerFirst)}”` : 'Write the text…'
  return `Wait ${waitLabel(s.days)}`
}

function blankStep(type: StepType): EditorStep {
  if (type === 'task') return { uid: uid(), type, title: '', due_days: 0 }
  if (type === 'sms') return { uid: uid(), type, body: '' }
  if (type === 'email') return { uid: uid(), type, subject: '', body: '' }
  return { uid: uid(), type, days: 2 }
}

function fill(text: string | undefined, officerFirst: string): string {
  return (text ?? '')
    .replace(/\{first_name\}/g, SAMPLE_NAME)
    .replace(/\{last_name\}/g, 'Lee')
    .replace(/\{full_name\}/g, `${SAMPLE_NAME} Lee`)
    .replace(/\{lo_first\}/g, officerFirst)
    .replace(/\{lo_name\}/g, officerFirst)
    .replace(/\{company\}/g, 'Buckingham Mortgage')
}

/** The problems the server would reject, found as the officer types, so Save
 *  rarely fails. Keyed by step uid ('' for the workflow as a whole). */
function problems(draft: { name: string; steps: EditorStep[] }, allowed: Set<string>): Map<string, string> {
  const out = new Map<string, string>()
  if (!draft.name.trim()) out.set('', 'Give the workflow a name.')
  if (!draft.steps.length) out.set('', 'Add at least one step.')
  else if (draft.steps.every((s) => s.type === 'wait')) out.set('', 'Add a task, text or email — a workflow can’t be only waits.')
  draft.steps.forEach((s, i) => {
    const prev = draft.steps[i - 1]
    const unknownField = [s.title, s.body, s.subject].join(' ').match(/\{(\w+)\}/g)
      ?.map((m) => m.slice(1, -1)).find((f) => !allowed.has(f))
    if (unknownField) out.set(s.uid, `{${unknownField}} can’t be filled in — use the buttons below the box instead.`)
    else if (s.type === 'task' && !s.title?.trim()) out.set(s.uid, 'Say what the task is.')
    else if (s.type === 'sms' && !s.body?.trim()) out.set(s.uid, 'Write the text.')
    else if (s.type === 'sms' && (s.body ?? '').length > SMS_MAX) out.set(s.uid, `Keep texts under ${SMS_MAX} characters.`)
    else if (s.type === 'email' && !s.subject?.trim()) out.set(s.uid, 'Add a subject line.')
    else if (s.type === 'email' && !s.body?.trim()) out.set(s.uid, 'Write the email.')
    else if (s.type === 'wait' && (!s.days || s.days < 1 || s.days > 365)) out.set(s.uid, 'A wait can be 1 to 365 days.')
    else if (s.type === 'wait' && prev?.type === 'wait') out.set(s.uid, 'Two waits in a row — combine them into one.')
    else if (s.type === 'wait' && i === draft.steps.length - 1) out.set(s.uid, 'Nothing happens after this wait. Remove it or add the next step.')
  })
  return out
}

export function WorkflowBuilder({ officerName }: { officerName: string }) {
  const { workflowId } = useParams()
  const [params] = useSearchParams()
  const copyFrom = params.get('from')
  const fromDraft = params.get('draft') === '1'
  const nav = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()
  const { data, error, loading } = useAsync(() => api.workflows(), [])
  const officerFirst = officerName.split(' ')[0] || 'you'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('My workflows')
  const [scope, setScope] = useState<'mine' | 'branch'>('mine')
  const [steps, setSteps] = useState<EditorStep[]>([])
  const [loaded, setLoaded] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const [audienceExpanded, setAudienceExpanded] = useState(false)
  const [serverError, setServerError] = useState('')
  const [insertAt, setInsertAt] = useState<number | null>(null)
  // One step is open for editing at a time; the rest read as a one-line schedule.
  const [openUid, setOpenUid] = useState<string | null>(null)
  // A draft Claude wrote from the officer's description (see Workflows › DescribeIt).
  const [aiDraft, setAiDraft] = useState<(WorkflowDraftResult & { prompt: string }) | null>(null)
  // Starting automatically sends real messages, so it is offered, never pre-ticked.
  const [autoStart, setAutoStart] = useState(false)
  // Optional on this page rather than a dialog afterwards: pick who it should run
  // for while you build it, and Save starts it for them.
  const [startFor, setStartFor] = useState<{ id: number; name: string | null }[]>([])
  // Opened from someone's Workflows panel: they are who this is being built for, so
  // they are already in the list and the section is open to show it.
  const forId = Number(params.get('for')) || null
  useEffect(() => {
    if (!forId) return
    let live = true
    api.contact(forId)
      .then((c) => { if (live) { setStartFor([{ id: forId, name: c.full_name }]); setAudienceExpanded(true) } })
      .catch(() => {})
    return () => { live = false }
  }, [forId])
  // The same list-and-filter view as a drip's audience, so picking a handful of
  // people and picking a whole segment are the same gesture.
  const [audience, setAudience] = useState<DripAudience>({ owner: 'mine' })
  const [people, setPeople] = useState<DripPreview | null>(null)
  const vocab = useVocabulary()
  const { data: dripMeta } = useAsync(() => api.drips(), [])

  const editing = workflowId ? data?.workflows.find((w) => w.id === Number(workflowId)) : undefined

  // Seed the form once the catalog arrives: an existing workflow, a copy of one,
  // or blank.
  useEffect(() => {
    if (!data || loaded) return
    if (fromDraft) {
      try {
        const stored = JSON.parse(sessionStorage.getItem('workflow-draft') ?? 'null')
        if (stored?.draft) {
          setName(stored.draft.name ?? '')
          setDescription(stored.draft.description ?? '')
          setCategory(data.categories.includes(stored.draft.category) ? stored.draft.category : 'My workflows')
          setSteps((stored.draft.steps ?? []).map((st: PlaybookStep) => ({ ...st, uid: uid() })))
          setAiDraft(stored)
          if (stored.problem) setServerError(stored.problem)
          setDirty(true)
        }
      } catch {
        /* a corrupt or missing draft just opens a blank builder */
      }
      setLoaded(true)
      return
    }
    const source = editing ?? (copyFrom ? data.workflows.find((w) => w.key === copyFrom) : undefined)
    if (source) {
      setName(editing ? source.name : `${source.name} (my version)`)
      setDescription(source.description ?? '')
      setCategory(data.categories.includes(source.category) ? source.category : 'My workflows')
      setScope(editing?.scope === 'branch' ? 'branch' : 'mine')
      setSteps(source.steps.map((s) => ({ ...s, uid: uid() })))
    }
    setLoaded(true)
  }, [data, editing, copyFrom, fromDraft, loaded])

  // The table lists everyone in view and narrows as filters are ticked.
  useEffect(() => {
    let live = true
    const t = window.setTimeout(() => {
      api.previewDrip(audience, []).then((r) => { if (live) setPeople(r) }).catch(() => { if (live) setPeople(null) })
    }, 250)
    return () => { live = false; window.clearTimeout(t) }
  }, [audience])

  // Leaving with unsaved work loses it; the browser can at least ask on reload/close.
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const allowed = useMemo(() => new Set((data?.merge_fields ?? []).map((f) => f.key)), [data])
  const issues = problems({ name, steps }, allowed)
  const touches = steps.filter((s) => s.type !== 'wait').length
  const dayOf: number[] = []
  steps.reduce((d, s) => { dayOf.push(d); return d + (s.type === 'wait' ? s.days ?? 0 : 0) }, 0)
  const days = steps.reduce((a, s) => a + (s.type === 'wait' ? s.days ?? 0 : 0), 0)

  const change = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setDirty(true) }
  const updateStep = (id: string, patch: Partial<PlaybookStep>) => {
    setSteps((xs) => xs.map((s) => (s.uid === id ? { ...s, ...patch } : s)))
    setDirty(true)
  }
  const addStep = (type: StepType, at: number | null) => {
    const fresh = blankStep(type)
    setSteps((xs) => {
      const next = [...xs]
      next.splice(at ?? xs.length, 0, fresh)
      return next
    })
    setOpenUid(fresh.uid)
    setInsertAt(null)
    setDirty(true)
  }
  // Drag a step by its grip to reorder. Pointer events rather than HTML5 drag
  // and drop: they work the same with a mouse, a trackpad and a touchscreen.
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragTo, setDragTo] = useState<number | null>(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (dragFrom === null) return
    // Drop before the first step whose middle is below the pointer.
    const onMove = (e: PointerEvent) => {
      const mids = rowRefs.current.map((el) => (el ? el.getBoundingClientRect().top + el.getBoundingClientRect().height / 2 : Infinity))
      const to = mids.findIndex((m) => e.clientY < m)
      setDragTo(to === -1 ? steps.length : to)
    }
    const onUp = () => {
      setDragFrom((from) => {
        setDragTo((to) => {
          if (from !== null && to !== null && to !== from && to !== from + 1) {
            setSteps((xs) => {
              const next = [...xs]
              const [moved] = next.splice(from, 1)
              next.splice(from < to ? to - 1 : to, 0, moved!)
              return next
            })
            setDirty(true)
          }
          return null
        })
        return null
      })
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      document.body.style.userSelect = ''
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [dragFrom, steps.length])

  const move = (i: number, by: -1 | 1) => {
    setSteps((xs) => {
      const j = i + by
      if (j < 0 || j >= xs.length) return xs
      const next = [...xs]
      ;[next[i], next[j]] = [next[j]!, next[i]!]
      return next
    })
    setDirty(true)
  }
  const remove = (id: string) => { setSteps((xs) => xs.filter((s) => s.uid !== id)); setDirty(true) }

  async function save() {
    setAttempted(true)
    if (issues.size) {
      setServerError('Fix the highlighted steps first.')
      const firstBad = steps.find((s) => issues.has(s.uid))
      if (firstBad) setOpenUid(firstBad.uid)
      return
    }
    setBusy(true)
    setServerError('')
    const draft: WorkflowDraft = {
      name: name.trim(), description: description.trim(), category, scope,
      steps: steps.map(({ uid: _uid, ...s }) => s),
    }
    try {
      const stored = editing?.id ? await api.updateWorkflow(editing.id, draft) : await api.createWorkflow(draft)
      setDirty(false)
      sessionStorage.removeItem('workflow-draft')
      const trigger = aiDraft?.trigger
      if (trigger && autoStart && !editing) {
        try {
          await api.setAutomation({ event: trigger.event, playbook: stored.key, scope: 'mine',
            filter: trigger.to ? { to: trigger.to } : null, active: true })
          toast(`Created “${draft.name}” — it will start automatically ${triggerText(trigger)}`, { tone: 'positive' })
        } catch (e) {
          toast(`Saved, but the automatic start couldn’t be set: ${errorText(e)}`, { tone: 'warning' })
        }
        nav('/workflows')
      } else {
        const started = await Promise.allSettled(
          startFor.map((p) => api.enroll(stored.key, { contact_id: p.id })))
        const ok = started.filter((s) => s.status === 'fulfilled').length
        const failed = started.length - ok
        toast(
          ok ? `${editing ? 'Saved' : `Created “${draft.name}”`} — started for ${ok} ${ok === 1 ? 'person' : 'people'}`
            : editing ? 'Workflow saved'
              : `Created “${draft.name}” — start it from any contact’s Start workflow button`,
          { tone: 'positive' })
        if (failed) toast(`${failed} couldn’t be started — they may already be on it`, { tone: 'warning' })
        nav(startFor.length === 1 && ok ? `/contacts/c/${startFor[0]!.id}` : '/workflows')
      }
    } catch (e) {
      setServerError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  if (loading && !data) return <div className="page"><Loading /></div>
  if (error || !data) return <div className="page"><ErrorState error={error} /></div>
  if (workflowId && !editing) {
    return <div className="page"><ErrorState error={new Error('That workflow doesn’t exist, or it isn’t yours to edit.')} /></div>
  }

  return (
    <div className="page workflow-builder">
      <div className="crumbs"><Link to="/workflows">Workflows</Link> › {editing ? 'Edit' : 'New workflow'}</div>
      <div className="page-head">
        <div>
          <h1 className="h1">{editing ? `Edit “${editing.name}”` : 'Build a workflow'}</h1>
          <p className="subline">
            Build the sequence. Preview the timing. Save when it feels right.
            {editing ? ' Changes apply to people you start it for from now on — anyone already on it finishes the old version.' : ''}
          </p>
        </div>
        <div className="builder-header-actions">
          <span className="builder-save-state">{busy ? 'Saving…' : dirty ? 'Unsaved changes' : 'Draft workflow'}</span>
          <button className="btn" onClick={async () => {
            if (!dirty || await confirm({
              title: 'Leave without saving?',
              body: 'The steps you’ve written here will be lost.',
              confirmLabel: 'Leave', cancelLabel: 'Keep editing', danger: true,
            })) nav('/workflows')
          }}>Cancel</button>
          <button className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Save workflow'}
          </button>
        </div>
      </div>

      {aiDraft ? (
        <div className="ai-banner">
          <Icon name="auto_awesome" size={20} style={{ color: 'var(--accent)', flex: 'none' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>Drafted from your description — read every message before you save</div>
            <div className="quiet" style={{ fontSize: 12.5, marginTop: 2 }}>“{aiDraft.prompt}”</div>
            {aiDraft.notes ? <div className="ai-notes">{aiDraft.notes}</div> : null}
            {aiDraft.trigger ? (
              <label className="checkrow" style={{ marginTop: 10 }}>
                <input type="checkbox" checked={autoStart} onChange={(e) => setAutoStart(e.target.checked)} />
                <span>Also start it automatically {triggerText(aiDraft.trigger)} <span className="hint">— for your contacts</span></span>
              </label>
            ) : null}
          </div>
          <Link to="/workflows?create=1" className="btn sm" style={{ flex: 'none' }}>Describe it again</Link>
        </div>
      ) : null}

      <div className="wb-grid">
        <div className="stack">
          {/* 1 · the basics */}
          <section className="card">
            <div className="card-head"><span className="wb-num">1</span><h2 className="h2">Workflow details</h2></div>
            <div className="card-body form-grid" style={{ paddingTop: 4 }}>
              <label className="fl span-2">
                <span>Workflow name</span>
                <input className={`field${attempted && issues.get('') && !name.trim() ? ' field-bad' : ''}`} value={name}
                  maxLength={60} placeholder="e.g. Open house follow-up"
                  onChange={(e) => change(setName)(e.target.value)} />
              </label>
              <label className="fl">
                <span>Category</span>
                <select className="field" value={category} onChange={(e) => change(setCategory)(e.target.value)}>
                  {data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="fl">
                <span>Visibility</span>
                <select className="field" value={scope} onChange={(e) => change(setScope)(e.target.value as 'mine' | 'branch')}>
                  <option value="mine">Just me</option>
                  {data.can_share ? <option value="branch">Everyone at the branch</option> : null}
                </select>
              </label>
              <label className="fl span-2">
                <span>Description <span className="hint">Optional</span></span>
                <input className="field" value={description} maxLength={240}
                  placeholder="e.g. Thank people who came to an open house, then follow up by phone."
                  onChange={(e) => change(setDescription)(e.target.value)} />
              </label>
            </div>
          </section>

          {/* 2 · the steps */}
          <section className="card">
            <div className="card-head">
              <span className="wb-num">2</span><h2 className="h2">Your sequence</h2>
              <span className="spacer" />
              <span className="quiet" style={{ fontSize: 12 }}>{touches} actions · {days} days</span>
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {steps.length === 0 ? (
                <div className="wb-empty">
                  <Icon name="route" size={26} style={{ color: 'var(--accent)' }} />
                  <div style={{ fontWeight: 600, marginTop: 6 }}>What should happen first?</div>
                  <div className="quiet" style={{ fontSize: 12.5, maxWidth: 420, margin: '4px auto 0' }}>
                    Most workflows start with a call or a text, then a wait, then the next touch.
                    Pick the first step below.
                  </div>
                </div>
              ) : null}

              {steps.map((s, i) => (
                <div key={s.uid} ref={(el) => { rowRefs.current[i] = el }}>
                  {i > 0 ? (
                    <InsertPoint open={insertAt === i} onOpen={() => setInsertAt(insertAt === i ? null : i)}
                      onPick={(t) => addStep(t, i)} />
                  ) : null}
                  {dragFrom !== null && dragTo === i && dragFrom !== i && dragFrom !== i - 1
                    ? <div className="wb-dropline" /> : null}
                  <StepCard step={s} index={i} total={steps.length} problem={attempted ? issues.get(s.uid) : undefined}
                    day={dayOf[i] ?? 0} open={openUid === s.uid}
                    dragging={dragFrom === i}
                    onGrab={() => { setDragFrom(i); setDragTo(i); setOpenUid(null) }}
                    onToggle={() => setOpenUid(openUid === s.uid ? null : s.uid)}
                    fields={data.merge_fields} officerFirst={officerFirst}
                    onChange={(patch) => updateStep(s.uid, patch)} onMove={(by) => move(i, by)}
                    onRemove={() => remove(s.uid)} />
                </div>
              ))}

              {dragFrom !== null && dragTo === steps.length && dragFrom !== steps.length - 1
                ? <div className="wb-dropline" /> : null}

              <div className="wb-addbar">
                <span className="quiet" style={{ fontSize: 12.5 }}>{steps.length ? 'Then…' : 'Start with…'}</span>
                {(['task', 'sms', 'email', 'wait'] as StepType[]).map((t) => (
                  <button key={t} className={`btn wb-add k-${t}`} onClick={() => addStep(t, null)}
                    disabled={t === 'wait' && (steps.length === 0 || steps[steps.length - 1]?.type === 'wait')}
                    title={STEP_META[t].hint}>
                    <Icon name={STEP_META[t].icon} size={16} /> {STEP_META[t].label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* optionally start it for people, right here */}
          <section className="card">
            <button type="button" className="builder-audience-toggle" aria-expanded={audienceExpanded} onClick={()=>setAudienceExpanded(!audienceExpanded)}><span><strong>Start for contacts</strong><small>Optional. Save it to your library, or choose who starts now.</small></span><span>{startFor.length ? `${startFor.length} selected` : ''}<Icon name={audienceExpanded ? 'expand_less' : 'expand_more'} size={19}/></span></button>
            {audienceExpanded ?             <div className="card-body" style={{ paddingTop: 4 }}>
              <p className="quiet" style={{ margin: '0 0 8px', fontSize: 12.5 }}>
                {editing
                  ? 'Saving changes starts nothing by itself. Pick people here to start the saved version for them.'
                  : 'Everyone you pick starts today, all at once, and runs to the end unless you stop them. Leave it empty and the workflow just waits in your list.'}
              </p>
              <PeoplePicker selected={startFor} onChange={setStartFor} placeholder="Type a name, address or phone…" />
              <div className="quiet" style={{ fontSize: 12.5, margin: '12px 0 6px' }}>…or tick them below. Filter a column to narrow the list.</div>
              <PeopleTable
                lists={[
                  { key: 'lead_status' as FilterKey, label: 'Lead stage', options: vocab?.lead_status ?? [] },
                  { key: 'campaign' as FilterKey, label: 'Got a mailer', options: (dripMeta?.campaigns ?? []).map((c) => ({ key: c.campaign, label: `${c.campaign} (${c.leads})` })) },
                  { key: 'timeline' as FilterKey, label: 'Timeline', options: vocab?.timeline ?? [] },
                  { key: 'purpose' as FilterKey, label: 'Looking to', options: vocab?.purpose ?? [] },
                  { key: 'source' as FilterKey, label: 'How they found us', options: vocab?.source ?? [] },
                ]}
                audience={audience} preview={people} vocab={vocab}
                onFilter={(key, values) => setAudience((a) => ({ ...a, [key]: values }))}
                selected={new Set(startFor.map((p) => p.id))}
                onToggle={(id, name) => setStartFor((s) => (s.some((x) => x.id === id)
                  ? s.filter((x) => x.id !== id) : [...s, { id, name }]))}
                onToggleShown={(rows, checked) => setStartFor((s) => (checked
                  ? [...s, ...rows.filter((r) => !s.some((x) => x.id === r.id))]
                  : s.filter((x) => !rows.some((r) => r.id === x.id))))}
                emptyNote="Nobody matches every filter." />
              {startFor.length ? (
                <div className="quiet" style={{ fontSize: 12, marginTop: 8 }}>
                  Saving starts it for {startFor.length} {startFor.length === 1 ? 'person' : 'people'}
                  {data.messaging.mode === 'simulated' ? ' — messaging is in test mode, so nothing is delivered yet' : ''}.
                  <button className="linkbtn" style={{ marginLeft: 6 }} onClick={() => setStartFor([])}>Clear</button>
                </div>
              ) : null}
              {/* Past a handful, this is a campaign — and a campaign wants pacing and
                  stop rules, which is exactly what a drip adds on top of a workflow. */}
              {startFor.length >= 15 ? (
                <div className="notice accent" style={{ marginTop: 10 }}>
                  <Icon name="water_drop" />
                  <span>
                    That's {startFor.length} people in one go. A <Link to="/drips/new">drip campaign</Link> runs this same
                    workflow a few people a day and stops for anyone who replies or applies — safer for a group this size.
                  </span>
                </div>
              ) : null}
            </div> : null}
          </section>

          {serverError || (attempted && issues.get('')) ? (
            <div className="notice danger"><Icon name="error" /><span>{serverError || issues.get('')}</span></div>
          ) : null}
        </div>

        {/* 3 · the preview */}
        <aside className="wb-preview">
          <section className="card">
            <div className="card-head">
              <span className="panel-overline">THE CONTACT’S EXPERIENCE</span><h2 className="h2">Schedule preview</h2>
            </div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <p className="quiet" style={{ fontSize: 12.5, marginTop: 0 }}>
                A sample timeline for {SAMPLE_NAME}.
              </p>
              <Preview steps={steps} officerFirst={officerFirst} />
              <div className="wb-summary">
                {touches} step{touches === 1 ? '' : 's'} over {days === 0 ? 'one day' : `${days} days`}
              </div>
              {data.messaging.mode === 'simulated' && steps.some((s) => s.type === 'sms' || s.type === 'email') ? (
                <div className="notice warning" style={{ marginTop: 10 }}>
                  <Icon name="science" />
                  <span>Messaging is in test mode: texts and emails are recorded but not delivered yet.</span>
                </div>
              ) : null}
              <div className="builder-consent-note"><Icon name="verified_user" size={17}/><span>Consent and quiet hours are respected. A blocked message becomes a follow-up task.</span></div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}

function InsertPoint({ open, onOpen, onPick }: { open: boolean; onOpen: () => void; onPick: (t: StepType) => void }) {
  return (
    <div className="wb-connector">
      <span className="wb-line" />
      {open ? (
        <span className="wb-insert-menu">
          {(['task', 'sms', 'email', 'wait'] as StepType[]).map((t) => (
            <button key={t} className={`btn sm wb-add k-${t}`} onClick={() => onPick(t)}>
              <Icon name={STEP_META[t].icon} size={14} /> {STEP_META[t].label}
            </button>
          ))}
          <button className="icon-btn-plain" style={{ marginLeft: 0 }} onClick={onOpen} aria-label="Cancel" title="Cancel">
            <Icon name="close" size={16} />
          </button>
        </span>
      ) : (
        <button className="wb-insert" onClick={onOpen} title="Add a step here" aria-label="Add a step here">
          <Icon name="add" size={14} />
        </button>
      )}
    </div>
  )
}

const DUE_OPTIONS = [
  { v: 0, label: 'the same day' }, { v: 1, label: 'the next day' },
  { v: 2, label: 'in 2 days' }, { v: 7, label: 'in a week' },
]
// Slider stops: every day for the first week, then the lengths people actually
// pick. Uneven on purpose so "3 days" and "2 months" are both one easy drag.
const WAIT_STOPS = [1, 2, 3, 4, 5, 6, 7, 10, 14, 21, 30, 45, 60, 90, 120, 180, 365]
const nearestStop = (d: number | undefined) =>
  WAIT_STOPS.reduce((best, s, i) => (Math.abs(s - (d ?? 1)) < Math.abs(WAIT_STOPS[best]! - (d ?? 1)) ? i : best), 0)

function StepCard({ step, index, total, problem, day, open, onToggle, dragging, onGrab,
                   fields, officerFirst, onChange, onMove, onRemove }: {
  step: EditorStep
  index: number
  total: number
  problem?: string
  day: number
  open: boolean
  onToggle: () => void
  dragging: boolean
  onGrab: () => void
  fields: Option[]
  officerFirst: string
  onChange: (patch: Partial<PlaybookStep>) => void
  onMove: (by: -1 | 1) => void
  onRemove: () => void
}) {
  const meta = STEP_META[step.type]
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  // Where "+ Their first name" goes: the field the officer was last typing in.
  const [target, setTarget] = useState<'body' | 'subject' | 'title'>(step.type === 'task' ? 'title' : 'body')

  // A wait sits on the line between steps as a slider: drag to change how long.
  if (step.type === 'wait') {
    return (
      <div className={`wb-wait${problem ? ' wb-bad' : ''}${dragging ? ' wb-dragging' : ''}`}>
        <div className="wb-waitrow">
          <span className="wb-grip" onPointerDown={onGrab} title="Drag to move this wait"
            role="button" aria-label="Drag to move this wait"><Icon name="drag_indicator" size={16} /></span>
          <Icon name="schedule" size={16} style={{ color: 'var(--ink-3)' }} />
          <span className="quiet">then wait</span>
          <input type="range" className="wb-slider" min={0} max={WAIT_STOPS.length - 1} step={1}
            value={nearestStop(step.days)} aria-label="How long to wait"
            aria-valuetext={waitLabel(step.days)}
            onChange={(e) => onChange({ days: WAIT_STOPS[Number(e.target.value)] })} />
          <span className="wb-waitval">{waitLabel(step.days)}</span>
          <StepTools index={index} total={total} onMove={onMove} onRemove={onRemove} />
        </div>
        {problem ? <div className="wb-problem">{problem}</div> : null}
      </div>
    )
  }

  const head = (
    <>
      <span className="wb-day">{day === 0 ? 'Start' : `Day ${day}`}</span>
      <span className="kind-chip"><Icon name={meta.icon} size={17} /></span>
    </>
  )

  // Closed: one line — when, what kind, what it says.
  if (!open) {
    return (
      <div className={`wb-row k-${step.type}${problem ? ' wb-bad' : ''}${dragging ? ' wb-dragging' : ''}`}>
        <span className="wb-grip" onPointerDown={onGrab} title="Drag to reorder"
          role="button" aria-label={`Drag to reorder step ${index + 1}`}><Icon name="drag_indicator" size={18} /></span>
        <button className="wb-row-main" onClick={onToggle}>
          {head}
          <span style={{ minWidth: 0, flex: 1 }}>
            <span className="kind-label" style={{ fontSize: 11.5, display: 'block' }}>{meta.label}</span>
            <span className="wb-row-text">{summary(step, officerFirst)}</span>
            {problem ? <span className="wb-problem" style={{ display: 'block', marginTop: 2 }}>{problem}</span> : null}
          </span>
          <Icon name="edit" size={16} style={{ color: 'var(--ink-4)' }} />
        </button>
        <StepTools index={index} total={total} onMove={onMove} onRemove={onRemove} />
      </div>
    )
  }

  const insert = (field: string) => {
    const el = target === 'body' ? bodyRef.current : target === 'subject' ? subjectRef.current : titleRef.current
    const current = (step[target] ?? '') as string
    const token = `{${field}}`
    const at = el?.selectionStart ?? current.length
    const end = el?.selectionEnd ?? at
    onChange({ [target]: current.slice(0, at) + token + current.slice(end) })
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(at + token.length, at + token.length)
    })
  }

  const chips = (
    <div className="wb-fields">
      <span className="hint">Personalize:</span>
      {fields.filter((f) => ['first_name', 'lo_first', ...(step.type === 'email' ? ['lo_name'] : [])].includes(f.key)).map((f) => (
        <button key={f.key} type="button" className="chip-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => insert(f.key)}>
          + {f.label}
        </button>
      ))}
    </div>
  )

  const len = (step.body ?? '').length
  const segments = Math.max(1, Math.ceil(len / 160))

  return (
    <div className={`wb-step k-${step.type}${problem ? ' wb-bad' : ''}`}>
      <div className="wb-step-head">
        {head}
        <span style={{ minWidth: 0 }}>
          <span className="kind-label" style={{ display: 'block' }}>{meta.label}</span>
          <span className="quiet" style={{ fontSize: 11.5 }}>{meta.hint}</span>
        </span>
        <span className="spacer" />
        <StepTools index={index} total={total} onMove={onMove} onRemove={onRemove} />
        <button className="btn sm" onClick={onToggle}>Done</button>
      </div>

      {step.type === 'task' ? (
        <div className="wb-fieldset">
          <input ref={titleRef} className="field" value={step.title ?? ''} maxLength={200} autoFocus={!step.title}
            placeholder="e.g. Call {first_name} to see how the house hunt is going"
            onFocus={() => setTarget('title')}
            onChange={(e) => onChange({ title: e.target.value })} aria-label="Task" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Due</span>
            <select className="field" style={{ width: 150 }} value={step.due_days ?? 0}
              onChange={(e) => onChange({ due_days: Number(e.target.value) })} aria-label="When the task is due">
              {DUE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <span className="spacer" />
            {chips}
          </div>
        </div>
      ) : null}

      {step.type === 'sms' ? (
        <div className="wb-fieldset">
          <textarea ref={bodyRef} className="field" rows={3} value={step.body ?? ''} autoFocus={!step.body}
            placeholder={`e.g. Hi {first_name}, it's {lo_first} from Buckingham Mortgage — ...`}
            onChange={(e) => onChange({ body: e.target.value })} aria-label="Text message" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {chips}
            <span className="spacer" />
            <span className="hint" style={{ color: len > SMS_MAX ? 'var(--danger)' : undefined }}>
              {len}/{SMS_MAX} · {segments} text{segments === 1 ? '' : 's'}
            </span>
          </div>
          {step.body ? <div className="wb-sample">“{fill(step.body, officerFirst)}”</div> : null}
        </div>
      ) : null}

      {step.type === 'email' ? (
        <div className="wb-fieldset">
          <input ref={subjectRef} className="field" value={step.subject ?? ''} maxLength={150} autoFocus={!step.subject}
            placeholder="Subject — e.g. Great meeting you, {first_name}"
            onFocus={() => setTarget('subject')}
            onChange={(e) => onChange({ subject: e.target.value })} aria-label="Email subject" />
          <textarea ref={bodyRef} className="field" rows={6} value={step.body ?? ''}
            placeholder={'Hi {first_name},\n\n...'}
            onFocus={() => setTarget('body')}
            onChange={(e) => onChange({ body: e.target.value })} aria-label="Email body" />
          {chips}
        </div>
      ) : null}

      {problem ? <div className="wb-problem">{problem}</div> : null}
    </div>
  )
}

function StepTools({ index, total, onMove, onRemove }: {
  index: number; total: number; onMove: (by: -1 | 1) => void; onRemove: () => void
}) {
  return (
    <span className="wb-tools">
      <button className="icon-btn-plain" style={{ marginLeft: 0 }} disabled={index === 0}
        onClick={() => onMove(-1)} title="Move up" aria-label="Move step up"><Icon name="arrow_upward" size={17} /></button>
      <button className="icon-btn-plain" style={{ marginLeft: 0 }} disabled={index === total - 1}
        onClick={() => onMove(1)} title="Move down" aria-label="Move step down"><Icon name="arrow_downward" size={17} /></button>
      <button className="icon-btn-plain" style={{ marginLeft: 0 }} onClick={onRemove}
        title="Remove this step" aria-label="Remove step"><Icon name="delete" size={17} /></button>
    </span>
  )
}

function Preview({ steps, officerFirst }: { steps: EditorStep[]; officerFirst: string }) {
  if (!steps.length) return <p className="quiet" style={{ fontSize: 12.5 }}>Your schedule will appear here as you add steps.</p>
  let day = 0
  const start = new Date()
  const rows: { day: number; step: EditorStep }[] = []
  for (const s of steps) {
    if (s.type === 'wait') day += s.days ?? 0
    else rows.push({ day, step: s })
  }
  const dateOf = (d: number) => {
    const x = new Date(start)
    x.setDate(x.getDate() + d)
    return x.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }
  return (
    <ol className="wb-plan">
      {rows.map(({ day: d, step: s }) => (
        <li key={s.uid}>
          <div className="wb-plan-when">{d === 0 ? 'Today' : `Day ${d}`}<span>{dateOf(d)}</span></div>
          <div style={{ minWidth: 0 }}>
            <div className="wb-plan-what">
              <span className={`k-${s.type}`} style={{ color: 'var(--k)', display: 'inline-flex', transform: 'translateY(2px)' }}>
                <Icon name={STEP_META[s.type].icon} size={14} />
              </span>
              {s.type === 'task' ? `You: ${fill(s.title, officerFirst) || '…'}`
                : s.type === 'sms' ? `Text to ${SAMPLE_NAME}`
                  : `Email to ${SAMPLE_NAME}: ${fill(s.subject, officerFirst) || '…'}`}
            </div>
            {s.type === 'sms' && s.body ? <div className="wb-plan-msg">{fill(s.body, officerFirst)}</div> : null}
          </div>
        </li>
      ))}
    </ol>
  )
}


/** "when a lead moves to a stage to Application" → "when a lead moves to Application". */
function triggerText(t: { event_label: string; to: string }): string {
  const clause = t.event_label.charAt(0).toLowerCase() + t.event_label.slice(1)
  return t.to ? `when ${clause.replace(/ to a stage$/, '')} to ${titleCase(t.to)}` : `when ${clause}`
}
